/**
 * Localization table for all user-visible HUD strings.
 * No hardcoded strings in UI code — everything goes through getString().
 */

const STRINGS: Record<string, Record<string, string>> = {
  en: {
    // HUD labels
    'hud.health.label': 'HP',
    'hud.mana.label': 'MP',
    'hud.health.low_warning': 'LOW HP!',

    // Ability names
    'hud.ability.fireball': 'Fireball',
    'hud.ability.heal': 'Heal',
    'hud.ability.dash': 'Dash',
    'hud.ability.shield': 'Shield',
    'hud.ability.ready': 'READY',

    // Fainting overlay (formerly "death" in v1; players never die in v2)
    'hud.death.title': 'You faint',
    'hud.death.subtitle': 'Waking at the hearth…',

    // Damage types
    'hud.damage.physical': 'Physical',
    'hud.damage.magical': 'Magical',

    // Loot pickup
    'hud.loot.pickup_prompt': 'Press E to pick up',
    'hud.loot.collected': 'Loot',
    'hud.loot.rarity.common': 'Common',
    'hud.loot.rarity.uncommon': 'Uncommon',
    'hud.loot.rarity.rare': 'Rare',
    'hud.loot.rarity.epic': 'Epic',
    'hud.loot.rarity.legendary': 'Legendary',
    'hud.loot.heal_pickup': '+{0}',
    'hud.loot.buff_pickup': 'Weapon Buff!',
    'hud.loot.buff_active': 'Weapon Buff',
    'hud.loot.buff_expiring': 'Buff expiring!',

    // Inventory & Equipment
    'hud.inventory.title': 'INVENTORY',
    'hud.inventory.toggle': 'Press I to toggle inventory',
    'hud.inventory.empty': 'No items',
    'hud.inventory.weight': 'Weight: {0} / {1}',
    'hud.inventory.weight_label': 'carried',
    'hud.inventory.equip': 'Click to equip',
    'hud.inventory.unequip': 'Click to unequip',
    'hud.equipment.weapon': 'Weapon',
    'hud.equipment.armor': 'Armor',
    'hud.equipment.accessory': 'Accessory',
    'hud.equipment.empty_slot': 'Empty',
    'hud.item.iron_sword': 'Iron Sword',
    'hud.item.steel_blade': 'Steel Blade',
    'hud.item.shadow_dagger': 'Shadow Dagger',
    'hud.item.excalibur': 'Excalibur',
    'hud.item.leather_armor': 'Leather Armor',
    'hud.item.chain_mail': 'Chain Mail',
    'hud.item.plate_armor': 'Plate Armor',
    'hud.item.dragonscale_armor': 'Dragonscale Armor',
    'hud.item.swift_ring': 'Swift Ring',
    'hud.item.amulet_of_vitality': 'Amulet of Vitality',
    'hud.item.crit_ring': 'Crit Ring',
    'hud.item.phoenix_pendant': 'Phoenix Pendant',
    'hud.item.food_bread': 'Hearth Bread',
    'hud.item.food_stew_verdana': 'Verdana Thicket Stew',
    'hud.item.food_stew_sablewind': 'Sablewind Ash Broth',
    'hud.item.food_stew_frostmere': 'Frostmere Cloth-Wrap Stew',
    'hud.item.food_stew_ember_mire': 'Ember Mire Char Stew',
    'hud.item.food_tea': 'Wax-Sealed Tea',
    'hud.item.food_feast': 'Communal Feast',
    'hud.item.food_pet_dish': 'Pet Treat',
    'hud.item.weapon_shard': 'Weapon Shard',
    'hud.equipment.bonus_damage': '+{0} DMG',
    'hud.equipment.bonus_defense': '+{0} DEF',
    'hud.equipment.bonus_speed': '+{0} SPD',
    'hud.equipment.bonus_maxhp': '+{0} HP',
    'hud.equipment.bonus_crit': '+{0}% CRIT',

    // Materials inventory
    'hud.materials.title': 'MATERIALS',
    'hud.materials.primary': 'Primary',
    'hud.materials.secondary': 'Secondary',
    'hud.materials.regional': 'Regional',
    'hud.materials.rare': 'Rare',
    'hud.materials.toggle': 'Press C to toggle materials',

    // Stats panel
    'hud.stats.title': 'PLAYER STATS',
    'hud.stats.runs_completed': 'Runs Completed',
    'hud.stats.runs_attempted': 'Runs Attempted',
    'hud.stats.best_time': 'Best Clear Time',
    'hud.stats.best_time_none': '--:--',
    'hud.stats.enemies_killed': 'Unburdened',
    'hud.stats.loot_collected': 'Loot Collected',
    'hud.stats.highest_floor': 'Highest Floor',
    'hud.stats.bosses_defeated': 'Guardians unburdened',
    'hud.stats.toggle': 'Press TAB for stats',
    'hud.stats.close': 'Press any key to close',

    // Pause menu
    'menu.pause.title': 'PAUSED',
    'menu.pause.resume': 'Resume',
    'menu.pause.settings': 'Settings',
    'menu.pause.quit_lobby': 'Quit to Lobby',

    // Settings menu
    'menu.settings.title': 'SETTINGS',
    'menu.settings.audio': 'Audio',
    'menu.settings.audio.master_volume': 'Master Volume',
    'menu.settings.audio.sfx_volume': 'SFX Volume',
    'menu.settings.audio.music_volume': 'Music Volume',
    'menu.settings.display': 'Display',
    'menu.settings.display.fullscreen': 'Fullscreen',
    'menu.settings.display.touch_controls': 'Show Touch Controls',
    'menu.settings.display.debug_overlay': 'Scene Hotspots (F2)',
    'menu.settings.controls': 'Controls',
    'menu.settings.controls.move': 'Move',
    'menu.settings.controls.move_keys': 'WASD / Arrows',
    'menu.settings.controls.light_attack': 'Light Attack',
    'menu.settings.controls.light_attack_key': 'J',
    'menu.settings.controls.heavy_attack': 'Heavy Attack',
    'menu.settings.controls.heavy_attack_key': 'K',
    'menu.settings.controls.dodge': 'Dodge',
    'menu.settings.controls.dodge_key': 'Space',
    'menu.settings.controls.interact': 'Interact',
    'menu.settings.controls.interact_key': 'E',
    'menu.settings.controls.inventory': 'Inventory',
    'menu.settings.controls.inventory_key': 'I',
    'menu.settings.controls.pause': 'Pause',
    'menu.settings.controls.pause_key': 'Esc',
    'menu.settings.back': 'Back',

    // Title screen — save slots (C5 wax-seal rendering, ui/c5/C5SaveSlots.ts)
    'title.slots.empty': 'New Journey',
    'title.slots.delete_mark': '✕',

    // Tutorial overlay
    'hud.tutorial.move': 'Use WASD or Arrow Keys to move around',
    'hud.tutorial.attack': 'Press J to attack enemies',
    'hud.tutorial.combo': 'Chain attacks for combo damage!',
    'hud.tutorial.dodge': 'Press Space to dodge enemy attacks',
    'hud.tutorial.loot': 'Press E near loot to pick it up',
    'hud.tutorial.loot_fallback': 'Defeat enemies to find loot drops',
    'hud.tutorial.inventory': 'Press I to open your inventory',
    'hud.tutorial.floor': 'Find the exit to reach the next floor',
    'hud.tutorial.extract': 'Return home when you are ready',
    'hud.tutorial.dismiss': 'Press Esc twice to skip tutorial',

    // C5 "Indigo & Ink" frame (docs/design/ui-c5-indigo-ink.md)
    'c5.vitals.health': 'vitality',
    'c5.vitals.mana': 'breath',
    'c5.day.band': 'DAY {0}',
    'c5.a11y.frame': "The traveler's console",
    'c5.a11y.day_wheel': 'Day wheel',
    'c5.a11y.vitals': 'Vitality and breath',
    'c5.a11y.slips': 'Notes',
    'c5.a11y.sockets': 'Quick slots',
    'c5.socket.empty': 'empty',

    // C5 Letterbox (Layer 3)
    'c5.letterbox.arrived': 'a letter has come',
    'c5.letterbox.socket.unread': 'A letter is waiting',
    'c5.letterbox.socket.archive': 'Letters you have read',
    'c5.letterbox.socket.empty': 'No letters yet',
    'c5.letterbox.day_stamp': 'DAY {0}',
    'c5.letterbox.gift': 'Tucked inside: {0}',
    'c5.letterbox.close': 'fold it away',
    'c5.letterbox.archive_title': 'Letters kept',
    'c5.letterbox.archive_empty': 'Nothing has come yet. It will.',
    'c5.letterbox.a11y.letter': 'An open letter',
    'c5.letterbox.a11y.archive': 'Letters you have kept',

    // C5 Layer 3 — the Traveler's Almanac (opens off the day-wheel)
    'c5.almanac.key': 'L',
    'c5.almanac.days_title': 'The Days',
    'c5.almanac.marks_title': "Today's Marks",
    'c5.almanac.day_stamp_label': 'Day {0}',
    'c5.almanac.today': 'today',
    'c5.almanac.earlier_days': 'and {0} days before these',
    'c5.almanac.moon_title': 'The Moon Tonight',
    'c5.almanac.moon.new': 'New Moon',
    'c5.almanac.moon.waxing_crescent': 'Waxing Crescent',
    'c5.almanac.moon.first_quarter': 'First Quarter',
    'c5.almanac.moon.waxing_gibbous': 'Waxing Gibbous',
    'c5.almanac.moon.full': 'Full Moon',
    'c5.almanac.moon.waning_gibbous': 'Waning Gibbous',
    'c5.almanac.moon.last_quarter': 'Last Quarter',
    'c5.almanac.moon.waning_crescent': 'Waning Crescent',
    'c5.almanac.moon.lit': '{0}% lit',
    'c5.almanac.marks_empty': 'Nothing written yet. The day is still blank paper.',
    'c5.almanac.mark.whisper': 'A whisper',
    'c5.almanac.stat.runs': 'journeys made',
    'c5.almanac.stat.unburdened': 'unburdened',
    'c5.almanac.stat.gathered': 'gathered',
    'c5.almanac.stat.depth': 'deepest floor',
    'c5.almanac.stat.walked': 'time spent walking',
    'c5.almanac.close': 'close the almanac',
    'c5.almanac.hint': 'open the almanac',
    'c5.a11y.almanac': "The traveler's almanac",
    'c5.a11y.day_wheel_button': 'Day wheel — press {0} to open the almanac',

    // C5 paper surfaces — journal spread, hearth slips, village board
    'journal.title': 'Journal',
    'journal.close': 'close',
    'hearth.chat.title': 'Hearth Whispers',
    'hearth.chat.prompt': 'Press Enter to write…',
    'board.title': 'Village Board',
    'board.empty': 'No notes pinned yet.',
    'board.close_hint': '[Esc] Close',
  },
};

let currentLocale = 'en';

export function setLocale(locale: string): void {
  if (STRINGS[locale]) {
    currentLocale = locale;
  }
}

export function getString(key: string): string {
  return STRINGS[currentLocale]?.[key] ?? STRINGS['en']?.[key] ?? key;
}
